/**
 * Security & sessions (§7.2), including 2FA enrolment. Enrolment is two steps by
 * necessity — the secret is only trustworthy once the user has proved their app
 * generates a matching code — which is a different thing from the kill switch's
 * one-click rule, and stays that way.
 */

import { useState } from 'react';
import { useApi, useMutation } from '../../hooks/useApi';
import { routes } from '../../api/routes';
import { useSession } from '../../session/SessionContext';
import type { Page, SessionRecord } from '../../api/types';
import { Card, Field, LiveRegion, Badge, Loading } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { localDateTime } from '../../lib/format';

interface EnrolStart {
  /** Returned exactly once, at enrolment. It is never readable again (§11.2). */
  secret: string;
  uri: string;
}

function TwoFactor() {
  const { me, refresh } = useSession();
  const [started, setStarted] = useState<EnrolStart | null>(null);
  const [code, setCode] = useState('');
  const start = useMutation<Record<string, unknown>, EnrolStart>(routes.auth.twoFactorEnroll);
  const confirm = useMutation<Record<string, unknown>, unknown>(routes.auth.twoFactorVerify);
  // TODO(api): needs POST /api/v1/auth/2fa/removal, which must refuse while the
  // user has an active listing or connected account (FR-1.4). Removal has
  // preconditions, so it is not a DELETE of the enrolment.

  const enabled = Boolean(me?.totp_enabled_at);

  return (
    <Card title="Two-factor authentication">
      <p>
        Status: <Badge tone={enabled ? 'ok' : 'warn'}>{enabled ? 'on' : 'off'}</Badge>
      </p>

      {enabled ? (
        <>
          <p className="muted">Enabled {localDateTime(me?.totp_enabled_at ?? null)}.</p>
          {/*
            Visible and unavailable rather than absent: turning 2FA off is a real
            thing this account may be entitled to, just not from here yet (§7.4
            rule 3), so the reason is stated instead of the control vanishing.
          */}
          <button type="button" className="btn btn-danger" disabled aria-describedby="twofa-off-why">
            Turn off two-factor
          </button>
          <p className="muted" id="twofa-off-why">
            Not available yet. FR-1.4 makes two-factor mandatory for any account with an active
            listing or a connected account, so switching it off has to check those first.
          </p>
        </>
      ) : started ? (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if ((await confirm.run({ totp: code })) !== null) {
              setStarted(null);
              await refresh();
            }
          }}
        >
          <p>Add this secret to your authenticator app, then enter the code it shows.</p>
          <p className="mono secret">{started.secret}</p>
          <Field id="2fa-code" label="Six-digit code">
            <input
              id="2fa-code"
              className="input input-narrow"
              inputMode="numeric"
              pattern="\d{6}"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          {confirm.problem ? <InlineError problem={confirm.problem} /> : null}
          <button type="submit" className="btn btn-primary" disabled={confirm.pending}>
            Confirm and turn on
          </button>
        </form>
      ) : (
        <>
          <button
            type="button"
            className="btn btn-primary"
            disabled={start.pending}
            onClick={async () => {
              const s = await start.run({});
              if (s) setStarted(s);
            }}
          >
            Set up two-factor
          </button>
          {start.problem ? <InlineError problem={start.problem} /> : null}
        </>
      )}
    </Card>
  );
}

interface PasswordChanged {
  changed: boolean;
  /** True whenever the change went through: every other session is ended. */
  other_sessions_revoked: boolean;
}

export function Security() {
  const sessions = useApi<Page<SessionRecord>>(routes.auth.sessions);
  const revoke = useMutation<{ id: string }, unknown>((a) => routes.auth.session(a.id), {
    method: 'DELETE',
  });
  const changePassword = useMutation<Record<string, unknown>, PasswordChanged>(routes.auth.password);
  const [pw, setPw] = useState({ current_password: '', new_password: '' });

  return (
    <div className="narrow">
      <TwoFactor />

      <Card title="Password">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if ((await changePassword.run(pw)) !== null) {
              setPw({ current_password: '', new_password: '' });
              sessions.reload();
            }
          }}
        >
          <Field id="pw-old" label="Current password">
            <input
              id="pw-old"
              className="input"
              type="password"
              autoComplete="current-password"
              required
              value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
            />
          </Field>
          <Field
            id="pw-new"
            label="New password"
            hint="At least 12 characters, and not one that appears in a known breach."
          >
            <input
              id="pw-new"
              className="input"
              type="password"
              autoComplete="new-password"
              minLength={12}
              required
              value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            />
          </Field>
          <p className="muted">
            Changing this signs out every other session. The one you are using now stays.
          </p>
          {changePassword.problem ? <InlineError problem={changePassword.problem} /> : null}
          <button type="submit" className="btn btn-primary" disabled={changePassword.pending}>
            Change password
          </button>
          {changePassword.data ? (
            <LiveRegion>
              {changePassword.data.other_sessions_revoked
                ? 'Password changed. Every other session has been signed out.'
                : 'Password changed.'}
            </LiveRegion>
          ) : null}
        </form>
      </Card>

      <Card title="Active sessions">
        {sessions.initialLoading ? <Loading label="Loading sessions" /> : null}
        {sessions.problem ? <ErrorView problem={sessions.problem} onRetry={sessions.reload} /> : null}
        <ul className="session-list">
          {(sessions.data?.data ?? []).map((s) => (
            <li key={s.id} className="session">
              <div>
                <p>
                  {s.user_agent || 'Unknown device'} {s.current ? <Badge tone="ok">this one</Badge> : null}
                </p>
                <p className="muted">
                  {s.ip ?? 'unknown IP'} · last seen {localDateTime(s.last_seen_at)}
                </p>
              </div>
              {s.current ? null : (
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={revoke.pending}
                  onClick={async () => {
                    await revoke.run({ id: s.id });
                    sessions.reload();
                  }}
                >
                  Sign this one out
                </button>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
